import { logger } from "@/core/logging";

import pkg from "../../../package.json";
import { SearchParamsRecord } from "../tracking";

const LOCAL_ADMIN_BASE_URL = "http://localhost:5300";
const DEVELOPMENT_ADMIN_BASE_URL = "https://admin.dev.ageless.ai";
const PRODUCTION_ADMIN_BASE_URL = "https://admin.ageless.ai";
const LOCAL_AGELESS_BASE_URL = "http://localhost:3000";
const DEVELOPMENT_AGELESS_BASE_URL = "https://dev.ageless.ai";
const PRODUCTION_AGELESS_BASE_URL = "https://ageless.ai";
const PRODUCTION_DOMAIN = "ageless.ai";
const DEVELOPMENT_DOMAIN = "dev.ageless.ai";
const SNAP_PREVIEW_HOST_PATTERN = /^(\d+)\.snap\.ageless\.ai$/u;

export function isSnapPreviewHostname(hostname: string): boolean {
  return SNAP_PREVIEW_HOST_PATTERN.test(hostname);
}

function matchesDomain(hostname: string, domain: string): boolean {
  return hostname === domain || hostname.endsWith(`.${domain}`);
}

function isDevelopmentAgelessHostname(hostname: string): boolean {
  return matchesDomain(hostname, DEVELOPMENT_DOMAIN);
}

function isProductionAgelessHostname(hostname: string): boolean {
  // dev.ageless.ai is a subdomain of the production domain, so it has to be
  // ruled out here or every dev host reads as production.
  if (isSnapPreviewHostname(hostname) || isDevelopmentAgelessHostname(hostname)) {
    return false;
  }

  return matchesDomain(hostname, PRODUCTION_DOMAIN);
}

export const getHost = () => {
  if (process.env.NEXT_PUBLIC_ENVIRONMENT_NAME == "production") {
    return process.env.VERCEL_URL as string;
  }

  return process.env.NEXT_PUBLIC_VERCEL_BRANCH_URL || "localhost:3000";
};

export const getSiteUrl = (): string => {
  const raw = process.env.NEXT_PUBLIC_SITE_URL?.trim();
  const fallback = "https://localhost:3000";
  const isProd =
    process.env.VERCEL_ENV === "production" ||
    process.env.NEXT_PUBLIC_ENVIRONMENT_NAME === "production";

  if (!raw && isProd) {
    logger.warn("site_url_missing_in_production");
  }

  const value = raw ?? fallback;

  try {
    const url = new URL(value);
    // normalize: no trailing slash
    return url.toString().replace(/\/$/, "");
  } catch {
    logger.warn("invalid_site_url", { url: value || null });
  }

  return value;
};

/**
 * Returns an absolute URL for a given pathname using the provided base URL.
 */

export function getAbsoluteUrl(pathname: string, baseUrl: string): string {
  const path = pathname.startsWith("/") ? pathname : `/${pathname}`;
  return new URL(path, baseUrl).toString();
}

export const getAdminBaseUrl = (hostname?: string): string | undefined => {
  const configuredAdminBaseUrl = process.env.NEXT_PUBLIC_ADMIN_BASE_URL?.trim();
  if (configuredAdminBaseUrl) {
    return configuredAdminBaseUrl;
  }

  if (!hostname) {
    return undefined;
  }

  if (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "0.0.0.0" ||
    hostname.endsWith(".localhost.app") ||
    hostname.endsWith(".agelessai.local")
  ) {
    return LOCAL_ADMIN_BASE_URL;
  }

  const snapPreviewMatch = hostname.match(SNAP_PREVIEW_HOST_PATTERN);
  if (snapPreviewMatch?.[1]) {
    return `https://admin.${snapPreviewMatch[1]}.snap.repeatmd.app`;
  }

  if (isDevelopmentAgelessHostname(hostname)) {
    return DEVELOPMENT_ADMIN_BASE_URL;
  }

  if (isProductionAgelessHostname(hostname)) {
    return PRODUCTION_ADMIN_BASE_URL;
  }

  return undefined;
};

export function isLocalHttpHost(hostname: string): boolean {
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "0.0.0.0" ||
    hostname.endsWith(".localhost") ||
    hostname.endsWith(".localhost.app") ||
    hostname.endsWith(".agelessai.local")
  );
}

export const getAgelessAdminBaseUrl = (hostname?: string): string | undefined => {
  if (!hostname) {
    return undefined;
  }

  if (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "0.0.0.0" ||
    hostname.endsWith(".localhost.app") ||
    hostname.endsWith(".agelessai.local")
  ) {
    return LOCAL_AGELESS_BASE_URL;
  }

  if (isSnapPreviewHostname(hostname)) {
    return `https://${hostname}`;
  }

  if (isDevelopmentAgelessHostname(hostname)) {
    return DEVELOPMENT_AGELESS_BASE_URL;
  }

  if (isProductionAgelessHostname(hostname)) {
    return PRODUCTION_AGELESS_BASE_URL;
  }

  return undefined;
};

export const getAdminApiUrl = (hostname?: string) => {
  const adminBaseUrl = getAdminBaseUrl(hostname);
  if (!adminBaseUrl) {
    throw new Error("Unable to resolve the admin base URL.");
  }

  return `${adminBaseUrl}/api/v1`;
};
// Preview routes live under `/:lang/preview/...` (and potentially `/preview/...`).
export const isPreviewUrl = (url: string) => url.includes("/preview");

export const isProductionDomain = (): boolean => {
  if (typeof window === "undefined") return false;
  return isProductionAgelessHostname(window.location.hostname);
};

/**
 * Allowlist for debug tooling UI. This is intentionally hostname-based (not env-based)
 * so the UI can't accidentally show up on production domains even if the code ships.
 */
export const isDebugAssessmentPanelDomainAllowed = (hostname?: string): boolean => {
  const resolvedHostname =
    hostname ?? (typeof window !== "undefined" ? window.location.hostname : undefined);

  if (!resolvedHostname) return false;

  // PR preview environments (non-production) like:
  // - <PR_NUMBER>.snap.ageless.ai
  if (isSnapPreviewHostname(resolvedHostname)) {
    return true;
  }

  // Never allow on production domains.
  if (isProductionAgelessHostname(resolvedHostname)) {
    return false;
  }

  // Local dev
  if (
    resolvedHostname === "localhost" ||
    resolvedHostname === "127.0.0.1" ||
    resolvedHostname === "0.0.0.0"
  ) {
    return true;
  }

  // Brand subdomain dev helpers already used in this repo.
  if (
    resolvedHostname.endsWith(".localhost.app") ||
    resolvedHostname.endsWith(".agelessai.local")
  ) {
    return true;
  }

  return false;
};

/**
 * Extract brand name from hostname if it's a brand subdomain.
 * Supports localhost.app and agelessai.local domains.
 * @param host - The host header value (e.g., "amora.localhost.app" or "amora.agelessai.local:3000")
 * @returns The brand name if detected, null otherwise
 */
export function getBrandFromHost(host: string | null): string | null {
  if (!host) return null;
  // Remove port if present for matching
  const hostname = host.split(":")[0];
  const match = hostname.match(/^([^.]+)\.(localhost\.app|agelessai\.local)$/);
  return match?.[1] || null;
}

/**
 * Path validation utilities for brand subdomain routing
 */

/**
 * Check if a pathname is allowed on brand subdomains.
 * Allows only /LOCALE/ageless/* paths, plus Next.js internals and static assets.
 * @param pathname - The pathname to check (e.g., "/en-US/ageless" or "/en-US/ageless/step")
 * @returns true if the path is allowed, false otherwise
 */
export function isAllowedBrandPath(pathname: string): boolean {
  // Ignore Next internals & static assets
  if (
    pathname.startsWith("/_next") ||
    pathname.startsWith("/favicon") ||
    pathname.startsWith("/assets")
  ) {
    return true;
  }

  // Match dynamic locale: /LOCALE/ageless/*
  return /^\/([^/]+)\/ageless(\/.*)?$/.test(pathname);
}

/**
 * Extract locale from pathname.
 * @param pathname - The pathname to extract locale from (e.g., "/en-US/ageless")
 * @returns The locale code (e.g., "en-US") or "en-US" as default
 */
export function getLocaleFromPath(pathname: string): string {
  const match = pathname.match(/^\/([^/]+)/);
  return match?.[1] || pkg.i18n.defaultLang;
}

/** Request header the proxy sets to the request's own URL (see `proxy.ts`). */
export const INCOMING_URL_HEADER = "x-url";

export function getIncomingSearchParams(headers: Headers): URLSearchParams {
  const referer = headers.get("referer");
  const xUrl = headers.get(INCOMING_URL_HEADER);
  const rawUrl = xUrl || referer;
  if (!rawUrl) return new URLSearchParams();
  try {
    return new URL(rawUrl).searchParams;
  } catch {
    return new URLSearchParams();
  }
}

export function toURLSearchParams(record: SearchParamsRecord): URLSearchParams {
  return new URLSearchParams(
    Object.entries(record).flatMap(([k, v]) =>
      Array.isArray(v) ? v.map((val) => [k, val]) : v != null ? [[k, v]] : [],
    ),
  );
}
